import { useCallback, useEffect, useMemo, useState } from "react";

import { mostraMensagemErro } from "../../helpers/Notificacoes";

import { Cidade } from "../../dto/Cidade";
import { FormaCob } from "../../dto/FormaCob";
import { Agente } from "../../dto/Agentes";
import { PlanoPagto } from "../../dto/PlanoPagamento";
import { TabPreco } from "../../dto/TabPreco";
import { UF } from "../../dto/Uf";
import { UserInfo } from "../../dto/UserInfo";

import { AgentesService } from "../../services/AgentesService";
import { ItensService } from "../../services/ItensService";

import { useLocalidade } from "./localidade";
import { useNavigate } from "react-router-dom";

export const useAgente = () => {
    const navigate = useNavigate();
    const handleUnauthorized = useCallback(() => {
        navigate('/mvp', {
            state: {
                propsPage: 'login'
            }
        });
    }, [navigate]);

    const itensService = new ItensService();
    const agenteService = useMemo(() => new AgentesService(handleUnauthorized), [handleUnauthorized]);

    const [userInfo,] = useState<UserInfo>(JSON.parse(localStorage.getItem('udbs')!));
    const [agentes, setAgentes] = useState<Agente[]>([]);
    const [buscouAgentes, setBuscouAgentes] = useState<boolean>(false);
    const [agenteSelecionado, setAgenteSelecionado] = useState<Agente | null>();

    const [dadosCarregados, setDadosCarregados] = useState(false);

    const { cidades, setCidades, buscaCidadesUf, buscaCidadeById,
        uf, haveUf } = useLocalidade();

    const [cidadeSelecionada, setCidadeSelecionada] = useState<Cidade | null>();
    const [ufSelecionada, setUfSelecionada] = useState<UF | null>();
    const [tabPreco, setTabPreco] = useState<TabPreco[] | []>([]);
    const [buscouTabPreco, setBuscouTabPreco] = useState<boolean>(false);
    const [formaCob, setFormaCob] = useState<FormaCob[] | []>([]);
    const [buscouFormaCob, setBuscouFormaCob] = useState<boolean>(false);
    const [planoPagamento, setPlanoPagamento] = useState<PlanoPagto[] | []>([]);
    const [buscouPlanoPgto, setBuscouPlanoPgto] = useState<boolean>(false);
    const [tabPrecoSelecionada, setTabPrecoSelecionada] = useState<TabPreco | null>();
    const [formaCobSelecionada, setFormaCobSelecionada] = useState<FormaCob | null>();
    const [planoPagamentoSelecionado, setPlanoPagamentoSelecionado] = useState<PlanoPagto | null>();
    const [carregandoAgentes, setCarregandoAgentes] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [localidadesCarregadas, setLocalidadescarregadas] = useState(false);
    const [erros, setErros] = useState<{ [key: string]: string }>({});
    const [modoBloqueado, setModoBloqueado] = useState<boolean>(false);
    const [buscouDadosEmpresa, setBuscouDadosEmpresa] = useState<boolean>(false);
    const [dadosInvalidos, setDadosInvalidos] = useState<any[]>([]);

    const [dadosAgente, setDadosAgente] = useState<Agente>(
        {
            idEmpresa: agenteSelecionado?.idEmpresa!,
            codAgente: '',
            nome: agenteSelecionado?.nome!,
            nomeFantasia: agenteSelecionado?.nomeFantasia!,
            pessoaJuridica: agenteSelecionado?.pessoaJuridica!,
            cnpjCpf: agenteSelecionado?.cnpjCpf!,
            logradouro: agenteSelecionado?.logradouro!,
            numero: agenteSelecionado?.numero!,
            complemento: agenteSelecionado?.complemento,
            bairro: agenteSelecionado?.bairro!,
            idCidade: cidadeSelecionada?.idCidade!,
            idUf: cidadeSelecionada?.idUf!,
            cep: agenteSelecionado?.cep,
            fone1: agenteSelecionado?.fone1,
            inscricaoEstadual: agenteSelecionado?.inscricaoEstadual,
            email: agenteSelecionado?.email,
            idAgenteRepresentante: agenteSelecionado?.idAgenteRepresentante,
            tipoCliente: agenteSelecionado?.tipoCliente!,
            tipoRepresentante: agenteSelecionado?.tipoRepresentante!,
            dataLancamento: agenteSelecionado?.dataLancamento!,
            idPlanoPagto: agenteSelecionado?.idPlanoPagto,
            idFormaCob: agenteSelecionado?.idFormaCob,
            ativo: agenteSelecionado?.ativo,
            idTabPreco: agenteSelecionado?.idTabPreco!,
            idAgente: agenteSelecionado?.idAgente!,
            representantePrecoManu: agenteSelecionado?.representantePrecoManu!,
            representanteEditaAgen: agenteSelecionado?.representanteEditaAgen
        }
    );


    useEffect(() => {
        const buscaAgentes = async () => {
            if (!userInfo?.idAgente) {
                setCarregandoAgentes(false);
                return;
            }

            try {
                const response = await agenteService.get(`/v1/agente?agenteRepresentante=${userInfo.idAgente}`);

                // Corrige o nome da propriedade para bater com a sua interface ResponseMessage
                // (geralmente 'status' guarda o 200, 400, 404, 500, etc.)
                if ((response.codigo ?? 500) >= 400) {
                    setErro(response.mensagem || 'Erro ao buscar agentes da API.');
                    return;
                }

                // Mantive a estrutura que você usava no original (onde os dados vinham dentro de response.status.agentes)
                // Mas adicionei um fallback caso venham em response.data
                const listaAgentes = response.status?.agentes || [];

                if (listaAgentes.length === 0) {
                    setErro('Nenhum agente encontrado.');
                }

                setAgentes(listaAgentes);
                setBuscouAgentes(true);

            } catch (error: any) {
                console.error("Erro interno no hook:", error);
                setErro(error.message || "Ocorreu um erro inesperado na montagem da tela.");
            } finally {
                setCarregandoAgentes(false);
            }
        };

        buscaAgentes();
    }, [userInfo]);

    async function verificaAgenteExiste(doc: string): Promise<boolean> {
        const response = await agenteService.get(`/v1/agente?cnpjCpf=${doc}`);
        if (!response.status.agentes || response.status.agentes.length === 0) {
            return false;
        }
        return true;
    }

    useEffect(() => {
        const buscaTabPreco = async () => {
            if (!buscouTabPreco) {
                try {

                    const response = await itensService.get('/v1/tabela-preco/empresa');
                    if (!Array.isArray(response)) {
                        return;
                    }

                    const dados = response;

                    if (dados.length === 0) {
                        setErro("Nenhuma tabela de preço encontrada.");
                    }

                    setTabPreco(dados);
                    setBuscouTabPreco(true);

                    return response;
                } catch (error) {
                    console.error('Erro ao buscar tabelas de preço:', error);
                    setErro('Erro ao buscar tabelas de preço.');
                }
            }
            return tabPreco;
        };

        buscaTabPreco();
    }, [userInfo]);


    useEffect(() => {
        const buscaFormaCob = async () => {
            if (!buscouFormaCob) {
                try {
                    const response = await itensService.get('/v1/forma-cob/empresa');
                    if (!Array.isArray(response)) {
                        return;
                    }

                    const dados = response;

                    if (dados.length === 0) {
                        setErro('Nenhuma forma de cobrança encontrada.');
                    }

                    setFormaCob(dados);
                    setBuscouFormaCob(true);

                    return response;
                } catch (error) {
                    console.error('Erro ao buscar formas de cobrança:', error);
                    setErro('Erro ao buscar formas de cobrança.');
                    throw error;
                }
            }
            return formaCob;
        }

        buscaFormaCob();
    }, [userInfo]);


    useEffect(() => {
        const buscaPlanoPagto = async () => {
            if (!buscouPlanoPgto) {
                try {
                    const response = await itensService.get('/v1/plano-pagto/empresa');
                    if (!Array.isArray(response)) {
                        return;
                    }

                    const dados = response;

                    if (dados.length === 0) {
                        setErro('Nenhum plano de pagamento encontrado.');
                    }

                    setPlanoPagamento(dados);
                    setBuscouPlanoPgto(true);

                    return response;
                } catch (error) {
                    console.error('Erro ao buscar planos de pagamento:', error);
                    setErro('Erro ao buscar planos de pagamento.');
                    throw error;
                }
            }
            return planoPagamento;
        }
        buscaPlanoPagto();
    }, [userInfo]);

    useEffect(() => {
        setBuscouAgentes(false);
        setBuscouTabPreco(false);
        setBuscouFormaCob(false);
        setBuscouPlanoPgto(false);
    }, [userInfo]);

    function confereDadosEmpresa(): any[] {

        const dados: any[] = [];

        if (formaCob.length === 0) {
            dados.push({ titulo: 'Empresa', mensagem: ['Empresa não possui nenhuma forma de cobrança cadastrada.'] });
        }
        if (planoPagamento.length === 0) {
            dados.push({ titulo: 'Empresa', mensagem: ['Empresa não possui nenhum plano de pagamento cadastrado.'] });
        }
        if (tabPreco.length === 0) {
            dados.push({ titulo: 'Empresa', mensagem: ['Empresa não possui nenhuma tabela de preço cadastrada.'] });
        }

        return dados;
    }

    useEffect(() => {
        if (buscouFormaCob && buscouPlanoPgto && buscouTabPreco) {
            setBuscouDadosEmpresa(true);
            const dadosInvalidos = confereDadosEmpresa();
            if (dadosInvalidos.length > 0) {
                setModoBloqueado(true);
            }
            setDadosInvalidos(dadosInvalidos);
        }
    }, [buscouFormaCob, buscouPlanoPgto, buscouTabPreco]);


    //Busca e seta os dados do pedido selecionado
    const buscaDadosAgente = async (agenteSelecionado: Agente) => {

        if (!agenteSelecionado) {
            setDadosCarregados(false);
            setAgenteSelecionado(null);
            return;
        }

        const tabPrecoSelecionada = tabPreco!.find(tabela => tabela.idTabPreco === agenteSelecionado!.idTabPreco!);
        const planoPgtoSelecionado = planoPagamento.find(planoPgto => planoPgto.idPlanoPagto === agenteSelecionado!.idPlanoPagto!);
        const formaCobSelecionada = formaCob.find(formaCob => formaCob.idFormaCob === agenteSelecionado!.idFormaCob!);

        setTabPrecoSelecionada(tabPrecoSelecionada);
        setPlanoPagamentoSelecionado(planoPgtoSelecionado);
        setFormaCobSelecionada(formaCobSelecionada);

        setDadosCarregados(true);

        return { tabPreco: tabPrecoSelecionada, planoPagto: planoPgtoSelecionado, formaCob: formaCobSelecionada }
    }


    async function carregaDadosAgente(agente: Agente) {
        try {
            const [dadosAgente, localidadeAgente] = await Promise.all([
                buscaDadosAgente(agente),
                buscaLocalidadeAgente(agente.idCidade)
            ]);

            return { dadosAgente, localidadeAgente };
        } catch (error) {
            console.error("Erro ao carregar os dados:", error);
            return { dadosAgente: null, localidadeAgente: null };
        }
    }


    async function buscaLocalidadeAgente(idCidade: string) {

        setIsLoading(true);

        if (uf.length === 0) {
            setDadosCarregados(false);
            return;
        }

        try {
            const cidadeSelecionada = await buscaCidadeById(idCidade);

            const ufSelecionada = uf.find(data => data.idUf === cidadeSelecionada.idUf);
            const cidades = await buscaCidadesUf(ufSelecionada!.idUf);

            setCidades(cidades);
            setUfSelecionada(ufSelecionada);
            setCidadeSelecionada(cidadeSelecionada);
            setLocalidadescarregadas(true);

            return { uf: ufSelecionada, cidades: cidades, cidadeSelecionada: cidadeSelecionada }

        } catch (erro: any) {
            setLocalidadescarregadas(false);
            mostraMensagemErro(erro);
            return null;
        }
        finally {
            setIsLoading(false);
            setDadosCarregados(true);
        }
    }


    return {
        agentes,
        carregandoAgentes,
        buscouAgentes,
        setAgentes,
        buscaDadosAgente,
        carregaDadosAgente,
        dadosAgente,
        agenteSelecionado,
        dadosCarregados,
        verificaAgenteExiste,
        setAgenteSelecionado,
        buscaCidadesUf,
        cidades,
        setCidades,
        uf, haveUf,
        cidadeSelecionada,
        setCidadeSelecionada,
        ufSelecionada,
        setUfSelecionada,
        buscaLocalidadeAgente,
        localidadesCarregadas,
        tabPreco,
        buscouTabPreco,
        tabPrecoSelecionada,
        setTabPrecoSelecionada,
        formaCob,
        buscouFormaCob,
        formaCobSelecionada,
        setFormaCobSelecionada,
        planoPagamento,
        buscouPlanoPgto,
        planoPagamentoSelecionado,
        setPlanoPagamentoSelecionado,
        isLoading,
        setIsLoading,
        erro,
        erros,
        setErros,
        modoBloqueado,
        setModoBloqueado,
        dadosInvalidos,
        setDadosInvalidos,
        buscouDadosEmpresa,
        confereDadosEmpresa
    };
};